/**
 * The Slack rendering of a `slack_announce.json`, as the CS channel will see it.
 *
 * Shared by the editor (beside the blocks, during review) and the deploying view
 * (read-only, after publish). One component rather than two because the whole value
 * of a preview is that it is the same rendering in both places: an operator who
 * approved what they saw during review must see that same thing when the release host
 * offers to post it.
 *
 * The rendering is `slack-blocks-to-jsx` rather than markup of our own: mrkdwn is
 * Slack's own dialect (`*bold*`, `<url|label>`, `:emoji:`), and a preview that shows
 * those as literal characters is exactly as useful as reading the JSON — which is the
 * one thing the editor exists to save the operator from.
 */
import { Alert, Paper, Typography } from '@mui/material';
import { Message, type Block } from 'slack-blocks-to-jsx';
import { parseAnnouncementBlocks } from '@finvari/deployer';

export function AnnouncementPreview({ json }: { json: string | undefined }) {
    const parsed = parseAnnouncementBlocks(json || '{}');
    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            {parsed.parseError ? (
                <Alert severity="error">Cannot parse JSON: {parsed.parseError}</Alert>
            ) : parsed.blocks.length === 0 ? (
                <Typography color="text.secondary">No blocks.</Typography>
            ) : (
                <SlackBlocks blocks={parsed.blocks} />
            )}
        </Paper>
    );
}

/**
 * Slack's rendering of a block list — the whole message here, one block at a time in
 * the editor. The message header (avatar, bot name, timestamp) is dropped: we have no
 * bot identity to show that would be truthful, and the blocks are the part under
 * review. The theme is pinned to light because the app is, and the renderer otherwise
 * follows the operator's OS preference, which would put a dark message in a light page.
 */
export function SlackBlocks({ blocks }: { blocks: unknown[] }) {
    return <Message blocks={blocks as Block[]} name="" logo="" theme="light" withoutWrapper />;
}
